/**
 * CLI Workflow Management
 *
 * Inspect workflow definitions and steps from the terminal.
 */

import fs from 'node:fs/promises';
import process from 'node:process';
import { resolveConfig, getArgValue, hasFlag } from './auth.js';
import {
  apiRequest,
  fetchWorkflowEnvironments,
  renderWorkflowUserError,
  resolveEnvironmentByNameOrId,
} from './cli-helpers.js';
import { fetchOrExit } from './api-fetch.js';
import { applyWorkflowTagFlags } from './tag.js';
import { specExtension } from './brand.js';
import { handleWorkflowCreate } from './workflow-create.js';
import { handleWorkflowGet } from './workflow-get.js';
import { handleWorkflowList } from './workflow-list.js';
import { handleWorkflowRun } from './workflow-run.js';
import { runWorkflowRuns } from './workflow-runs.js';
import { handleWorkflowSync } from './workflow-sync.js';
import { handleWorkflowGenerateApiSequence } from './workflow-api-sequence.js';
import { handleWorkflowApiSequence } from './workflow-api-sequence-cmds.js';
import { handleWorkflowSeedCreate } from './workflow-seed-create.js';
import { WORKFLOW_GUIDE_SECTIONS } from '@chatsdet/content/workflow-guide';
import { topoOrderNodes } from '@chatsdet/workflow-diff';
import { printGuide } from '@chatsdet/content';
import { USER_SETTABLE_WORKFLOW_STATUSES } from '@chatsdet/types';
import type { PullWorkflow } from '@chatsdet/canary-spec';
import { getNodeTitle, type WorkflowDetailResponse } from './workflow-format.js';

export { shouldPrintWorkflowUrlTrailer } from './workflow-get.js';

const WORKFLOW_STATUS_DESCRIPTIONS: Record<
  (typeof USER_SETTABLE_WORKFLOW_STATUSES)[number],
  string
> = {
  draft: 'not yet approved — no human has signed off on the spec',
  approved: 'a human verified the test checks the right thing',
};

const formatWorkflowStatusList = (): string =>
  USER_SETTABLE_WORKFLOW_STATUSES.map((s) => `${s} (${WORKFLOW_STATUS_DESCRIPTIONS[s]})`).join(
    ', '
  );

/* ── Patch Handler ───────────────────────────────────────────────────── */

type WorkflowUpdateResponse = {
  ok: boolean;
  error?: string;
  workflow?: { id: string; name: string; status: string; flowType: string };
};

async function handlePatch(argv: string[], apiUrl: string, token: string): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Error: Missing workflow ID.');
    console.error(
      'Usage: canary workflow patch <workflowId> [--name ...] [--description ...] [--status ...] [--flow-type ...] [--default-environment <name|id>] [--clear-default-environment] [--any-environment] [--eligible-env-types permanent,sandbox] [--eligible-environments <names|ids>]'
    );
    process.exit(1);
  }

  const name = getArgValue(argv, '--name');
  const description = getArgValue(argv, '--description');
  const status = getArgValue(argv, '--status');
  const flowType = getArgValue(argv, '--flow-type');
  const seedOutputsJsonRaw = getArgValue(argv, '--seed-outputs-json');
  const seedOutputsFile = getArgValue(argv, '--seed-outputs-file');
  const jsonOutput = hasFlag(argv, '--json');

  // Environment config flags (resolved name→id against the flow's property).
  const defaultEnvironment = getArgValue(argv, '--default-environment');
  const clearDefaultEnvironment = hasFlag(argv, '--clear-default-environment');
  const anyEnvironment = hasFlag(argv, '--any-environment');
  const eligibleEnvTypes = getArgValue(argv, '--eligible-env-types');
  const eligibleEnvironments = getArgValue(argv, '--eligible-environments');
  const hasEnvFlag =
    defaultEnvironment !== undefined ||
    clearDefaultEnvironment ||
    anyEnvironment ||
    eligibleEnvTypes !== undefined ||
    eligibleEnvironments !== undefined;

  const hasTagFlag =
    getArgValue(argv, '--tags') !== undefined ||
    getArgValue(argv, '--tags-add') !== undefined ||
    getArgValue(argv, '--tags-remove') !== undefined ||
    getArgValue(argv, '--tag-id') !== undefined;

  if (
    !name &&
    !description &&
    !status &&
    !flowType &&
    !seedOutputsJsonRaw &&
    !seedOutputsFile &&
    !hasTagFlag &&
    !hasEnvFlag
  ) {
    console.error(
      'Error: No fields to update. Provide at least one of: --name, --description, --status, --flow-type, --seed-outputs-json, --seed-outputs-file, --tags, --tags-add, --tags-remove, --tag-id, --default-environment, --clear-default-environment, --any-environment, --eligible-env-types, --eligible-environments'
    );
    process.exit(1);
  }

  if (anyEnvironment && (eligibleEnvTypes !== undefined || eligibleEnvironments !== undefined)) {
    console.error(
      'Error: --any-environment is mutually exclusive with --eligible-env-types / --eligible-environments.'
    );
    process.exit(1);
  }
  if (clearDefaultEnvironment && defaultEnvironment !== undefined) {
    console.error(
      'Error: --clear-default-environment is mutually exclusive with --default-environment.'
    );
    process.exit(1);
  }

  if (status && !(USER_SETTABLE_WORKFLOW_STATUSES as readonly string[]).includes(status)) {
    console.error(`Error: Invalid status "${status}". Valid: ${formatWorkflowStatusList()}`);
    process.exit(1);
  }

  const validFlowTypes = ['standard', 'setup', 'seed', 'teardown'];
  if (flowType && !validFlowTypes.includes(flowType)) {
    console.error(
      `Error: Invalid flow type "${flowType}". Must be one of: ${validFlowTypes.join(', ')}`
    );
    process.exit(1);
  }

  const body: Record<string, unknown> = {};
  if (name) body.name = name;
  if (description) body.description = description;
  if (status) body.status = status;
  if (flowType) body.flowType = flowType;

  if (seedOutputsJsonRaw || seedOutputsFile) {
    let raw: string;
    if (seedOutputsFile) {
      try {
        raw = await fs.readFile(seedOutputsFile, 'utf-8');
      } catch (err) {
        console.error(
          `Error reading ${seedOutputsFile}: ${err instanceof Error ? err.message : String(err)}`
        );
        process.exit(1);
      }
    } else {
      raw = seedOutputsJsonRaw!;
    }
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        throw new Error('seedOutputsJson must be a JSON array');
      }
      body.seedOutputsJson = parsed;
    } catch (err) {
      console.error(
        `Error: Invalid --seed-outputs-json: ${err instanceof Error ? err.message : String(err)}`
      );
      process.exit(1);
    }
  }

  // Environment config: resolve named envs against the flow's property (the
  // GET payload already carries `environments` + eligibility), then build the
  // `defaultEnvironmentId` / `environmentConstraints` body fields.
  if (hasEnvFlag) {
    if (anyEnvironment) body.environmentConstraints = null;
    if (eligibleEnvTypes !== undefined) {
      const types = eligibleEnvTypes
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const valid = ['permanent', 'ephemeral', 'local', 'sandbox'];
      const bad = types.filter((t) => !valid.includes(t));
      if (bad.length > 0) {
        console.error(`Error: invalid env type(s): ${bad.join(', ')}. Valid: ${valid.join(', ')}`);
        process.exit(1);
      }
      body.environmentConstraints = { mode: 'envTypes', types };
    }
    if (clearDefaultEnvironment) body.defaultEnvironmentId = null;

    const needsResolution = defaultEnvironment !== undefined || eligibleEnvironments !== undefined;
    if (needsResolution) {
      const envs = await fetchWorkflowEnvironments(apiUrl, token, workflowId);
      if (defaultEnvironment !== undefined) {
        body.defaultEnvironmentId = resolveEnvironmentByNameOrId(envs, defaultEnvironment);
      }
      if (eligibleEnvironments !== undefined) {
        const ids = eligibleEnvironments
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
          .map((nameOrId) => resolveEnvironmentByNameOrId(envs, nameOrId));
        body.environmentConstraints = { mode: 'only', environmentIds: ids };
      }
    }
  }

  let tagResult: Awaited<ReturnType<typeof applyWorkflowTagFlags>> = null;

  if (Object.keys(body).length > 0) {
    const result = await apiRequest<WorkflowUpdateResponse>(
      apiUrl,
      token,
      'PUT',
      `/workflows/${workflowId}`,
      body
    );

    if (!result.ok) {
      if (jsonOutput) {
        console.log(JSON.stringify({ ok: false, error: result.error ?? 'Unknown error' }, null, 2));
      } else {
        console.error(`Error: ${result.error ?? 'Unknown error'}`);
      }
      process.exit(1);
    }
  }

  if (hasTagFlag) {
    try {
      tagResult = await applyWorkflowTagFlags({ apiUrl, token, argv, workflowId });
    } catch (err) {
      if (jsonOutput) {
        console.log(
          JSON.stringify(
            { ok: false, error: err instanceof Error ? err.message : String(err) },
            null,
            2
          )
        );
      } else {
        console.error(`Error applying tags:\n${err instanceof Error ? err.message : String(err)}`);
      }
      process.exit(1);
    }
  }

  if (jsonOutput) {
    console.log(
      JSON.stringify(
        { ok: true, workflowId, updated: body, tags: tagResult?.tagIds ?? null },
        null,
        2
      )
    );
    return;
  }

  const updatedKeys = Object.keys(body);
  if (updatedKeys.length > 0) {
    console.log(`Workflow ${workflowId} updated (${updatedKeys.join(', ')}).`);
  }
  if (tagResult) {
    console.log(`Tags: ${tagResult.tagIds.length} assigned.`);
  }
}

/* ── Archive / Unarchive Handlers ────────────────────────────────────── */

async function handleArchive(argv: string[], apiUrl: string, token: string): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Error: Missing workflow ID.');
    console.error('Usage: canary workflow archive <workflowId> [--yes] [--json]');
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, '--json');
  const skipConfirm = hasFlag(argv, '--yes', '-y');

  if (!skipConfirm) {
    if (!process.stdin.isTTY) {
      console.error(
        'Error: Archiving requires confirmation. Pass --yes (or -y) for non-interactive use.'
      );
      process.exit(1);
    }
    process.stdout.write(
      `Archive workflow ${workflowId}? It will be hidden from lists and skipped by test suites. [y/N] `
    );
    const answer = await new Promise<string>((resolve) => {
      process.stdin.setEncoding('utf-8');
      process.stdin.once('data', (chunk) => resolve(String(chunk).trim().toLowerCase()));
    });
    if (answer !== 'y' && answer !== 'yes') {
      console.error('Aborted.');
      process.exit(1);
    }
  }

  type ArchiveResult = { ok: boolean; success?: boolean; archived?: boolean; error?: string };
  const result = await apiRequest<ArchiveResult>(
    apiUrl,
    token,
    'DELETE',
    `/workflows/${workflowId}`
  );

  if (result.error) {
    if (jsonOutput) {
      console.log(JSON.stringify(result, null, 2));
    } else {
      renderWorkflowUserError(result);
    }
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
  } else {
    console.log(
      `Archived workflow ${workflowId}. Use 'canary workflow unarchive ${workflowId}' to restore.`
    );
  }
}

async function handleUnarchive(argv: string[], apiUrl: string, token: string): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Error: Missing workflow ID.');
    console.error('Usage: canary workflow unarchive <workflowId> [--json]');
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, '--json');

  type UnarchiveResult = { ok: boolean; error?: string };
  const result = await apiRequest<UnarchiveResult>(
    apiUrl,
    token,
    'POST',
    `/workflows/${workflowId}/unarchive`,
    {}
  );

  if (result.error) {
    if (jsonOutput) {
      console.log(JSON.stringify(result, null, 2));
    } else {
      renderWorkflowUserError(result);
    }
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
  } else {
    console.log(`Restored workflow ${workflowId}.`);
  }
}

/* ── Approve / Promote / Pause Handlers ──────────────────────────────── */

const APPROVAL_GUIDANCE = [
  'Approving a test asserts that a HUMAN has verified it tests the right thing.',
  '',
  'If you are an agent running this command on behalf of a user:',
  '  - Describe to the user what this test does and what approval means.',
  '  - Confirm the user has personally verified the test is correct.',
  '  - Only re-run with --yes after the user explicitly confirms.',
  '',
  'Approval alone does NOT include the test in suites — a flow becomes eligible for',
  'suite runs once it is approved AND has passed at least once.',
].join('\n');

async function handleApprove(argv: string[], apiUrl: string, token: string): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Error: Missing workflow ID.');
    console.error('Usage: canary workflow approve <workflowId> [--yes] [--json]');
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, '--json');

  if (!hasFlag(argv, '--yes')) {
    console.error(APPROVAL_GUIDANCE);
    console.error(`\nRe-run with --yes to approve: canary workflow approve ${workflowId} --yes`);
    process.exit(1);
  }

  type ApproveResult = {
    ok: boolean;
    error?: string;
    code?: string;
    workflow?: { id: string; name: string; status: string; approvedAt: string | null };
  };
  const result = await apiRequest<ApproveResult>(
    apiUrl,
    token,
    'POST',
    `/workflows/${workflowId}/approve`,
    {}
  );

  if (result.error) {
    if (jsonOutput) {
      console.log(JSON.stringify(result, null, 2));
    } else {
      console.error(`Error: ${result.error}`);
    }
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(
      JSON.stringify({ ok: true, workflowId, workflow: result.workflow ?? null }, null, 2)
    );
    return;
  }
  console.log(`Workflow ${workflowId} approved.`);
  console.log(
    `It becomes eligible for suite runs once it has also passed at least once ` +
      `(run it with: canary workflow run ${workflowId}).`
  );
}

// Promote a flow into suite runs (promotion model / `workflow.eligibility` flag OFF).
// The CLI can't cheaply resolve the org's flag, so both `promote` and `pause`/`unpause`
// stay available; the server branches on the org model for pause/unpause.
async function handlePromote(argv: string[], apiUrl: string, token: string): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Error: Missing workflow ID.');
    console.error('Usage: canary workflow promote <workflowId> [--force] [--json]');
    process.exit(1);
  }

  const force = hasFlag(argv, '--force');
  const jsonOutput = hasFlag(argv, '--json');

  type PromoteResult = {
    ok: boolean;
    error?: string;
    code?: string;
    workflow?: { id: string; name: string; promotion: string | null };
  };
  const result = await apiRequest<PromoteResult>(
    apiUrl,
    token,
    'POST',
    `/workflows/${workflowId}/promote`,
    force ? { force: true } : {}
  );

  if (result.error) {
    if (jsonOutput) {
      console.log(JSON.stringify(result, null, 2));
    } else {
      const hint =
        result.code === 'FLOW_NOT_READY_TO_PROMOTE'
          ? '\n  Trigger a successful run first (canary workflow run <id>), or pass --force.'
          : result.code === 'PROMOTION_REQUIRES_APPROVAL'
            ? `\n  Approve the test first: canary workflow approve ${workflowId} --yes\n  (Approval asserts a human verified the test checks the right thing — it cannot be forced.)`
            : '';
      console.error(`Error: ${result.error}${hint}`);
    }
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(
      JSON.stringify({ ok: true, workflowId, workflow: result.workflow ?? null }, null, 2)
    );
    return;
  }
  console.log(`Workflow ${workflowId} promoted — it will run when test suites run.`);
}

async function handlePauseToggle(
  argv: string[],
  apiUrl: string,
  token: string,
  paused: boolean
): Promise<void> {
  const action = paused ? 'pause' : 'unpause';
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Error: Missing workflow ID.');
    console.error(`Usage: canary workflow ${action} <workflowId> [--reason "..."] [--json]`);
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, '--json');
  const reason = getArgValue(argv, '--reason');

  type PauseResult = {
    ok: boolean;
    error?: string;
    code?: string;
    workflow?: { id: string; name: string; paused: boolean };
  };
  const result = await apiRequest<PauseResult>(
    apiUrl,
    token,
    'POST',
    `/workflows/${workflowId}/${action}`,
    reason ? { reason } : {}
  );

  if (result.error) {
    if (jsonOutput) {
      console.log(JSON.stringify(result, null, 2));
    } else {
      console.error(`Error: ${result.error}`);
    }
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(
      JSON.stringify({ ok: true, workflowId, workflow: result.workflow ?? null }, null, 2)
    );
    return;
  }
  if (paused) {
    console.log(`Workflow ${workflowId} paused — excluded from suite runs until unpaused.`);
  } else {
    console.log(`Workflow ${workflowId} unpaused — it runs in suites again once eligible.`);
  }
}

/* ── Node Patch Handler ──────────────────────────────────────────────── */

function getAllArgValues(argv: string[], flag: string): string[] {
  const values: string[] = [];
  for (let i = 0; i < argv.length; i++) {
    if (argv[i] === flag && i + 1 < argv.length) {
      values.push(argv[i + 1]!);
      i++;
    }
  }
  return values;
}

async function handleNodePatch(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const workflowId = positional[0];
  const nodeId = positional[1];

  if (!workflowId || !nodeId) {
    console.error('Error: Missing workflow ID and/or node ID.');
    console.error(
      'Usage: canary workflow node patch <workflowId> <nodeId> --set-input key=value [--config \'{"..."}\'] [--json]'
    );
    process.exit(1);
  }

  const configArg = getArgValue(argv, '--config');
  const setInputArgs = getAllArgValues(argv, '--set-input');
  const jsonOutput = hasFlag(argv, '--json');

  if (!configArg && setInputArgs.length === 0) {
    console.error(
      'Error: No changes specified. Provide --set-input key=value and/or --config \'{"..."}\''
    );
    process.exit(1);
  }

  // Build configJson from flags
  let configJson: Record<string, unknown> = {};

  if (configArg) {
    try {
      configJson = JSON.parse(configArg) as Record<string, unknown>;
    } catch (e) {
      console.error(
        `Error: Invalid JSON in --config: ${e instanceof Error ? e.message : String(e)}`
      );
      process.exit(1);
    }
  }

  if (setInputArgs.length > 0) {
    const inputValues: Record<string, string> =
      (configJson.inputValues as Record<string, string>) ?? {};
    for (const arg of setInputArgs) {
      const eqIdx = arg.indexOf('=');
      if (eqIdx === -1) {
        console.error(`Error: --set-input must be key=value, got: "${arg}"`);
        process.exit(1);
      }
      inputValues[arg.slice(0, eqIdx)] = arg.slice(eqIdx + 1);
    }
    configJson.inputValues = inputValues;
  }

  // Fetch current state for before/after diff
  const getRes = await fetchOrExit(`${apiUrl}/workflows/${workflowId}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    token,
  });
  const getResult = (await getRes.json()) as WorkflowDetailResponse;
  if (!getResult.workflow) {
    console.error(`Error: Workflow not found.`);
    process.exit(1);
  }

  const existingNode = getResult.workflow.nodes.find((n) => n.id === nodeId);
  if (!existingNode) {
    console.error(`Error: Node ${nodeId} not found in workflow ${workflowId}.`);
    console.error('\nAvailable nodes:');
    for (const n of topoOrderNodes(getResult.workflow.nodes, getResult.workflow.edges ?? [])) {
      console.error(`  ${n.id}  [${n.nodeType}] ${getNodeTitle(n)}`);
    }
    process.exit(1);
  }

  // Apply the patch
  const patchRes = await fetchOrExit(`${apiUrl}/workflows/${workflowId}/nodes/${nodeId}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    token,
    body: JSON.stringify({ configJson }),
  });

  if (!patchRes.ok) {
    const errBody = await patchRes.text();
    if (jsonOutput) {
      console.log(errBody || JSON.stringify({ ok: false, status: patchRes.status }, null, 2));
    } else {
      let rendered: string | null = null;
      try {
        const parsed = JSON.parse(errBody) as Record<string, unknown>;
        rendered = renderWorkflowUserError(parsed);
      } catch {
        rendered = null;
      }
      console.error(rendered ?? `Error (${patchRes.status}): ${errBody.slice(0, 500)}`);
    }
    process.exit(1);
  }

  const patchResult = (await patchRes.json()) as { node: PullWorkflow['nodes'][number] };

  if (jsonOutput) {
    console.log(JSON.stringify({ ok: true, node: patchResult.node }, null, 2));
    return;
  }

  // Show before/after diff
  const title = getNodeTitle(existingNode);
  console.log(`Updated node [${existingNode.nodeType}] "${title}" (${nodeId}):\n`);

  const oldConfig = existingNode.configJson ?? {};
  for (const [key, newVal] of Object.entries(configJson)) {
    if (
      typeof newVal === 'object' &&
      newVal !== null &&
      typeof oldConfig[key] === 'object' &&
      oldConfig[key] !== null
    ) {
      // Diff nested objects (e.g. inputValues)
      const oldObj = oldConfig[key] as Record<string, unknown>;
      const newObj = newVal as Record<string, unknown>;
      for (const [subKey, subVal] of Object.entries(newObj)) {
        const oldVal = oldObj[subKey];
        if (oldVal !== subVal) {
          console.log(
            `  ${key}.${subKey}: ${JSON.stringify(oldVal ?? null)} -> ${JSON.stringify(subVal)}`
          );
        }
      }
    } else {
      const oldVal = oldConfig[key];
      if (oldVal !== newVal) {
        console.log(`  ${key}: ${JSON.stringify(oldVal ?? null)} -> ${JSON.stringify(newVal)}`);
      }
    }
  }
}

/* ── Help & Entry Point ───────────────────────────────────────────────── */

function printWorkflowHelp(): void {
  console.log(
    [
      'Usage: canary workflow <sub-command> [options]',
      '',
      '  Tip: Run "canary workflow guide" for the full authoring reference',
      '       (node types, playbooks, macros, seeds, assertions, best practices)',
      '',
      'Sub-commands:',
      '  guide [section] [--json]                    Authoring guide (node-types, macros, etc.)',
      '  list [options]                              List workflows',
      '  get <workflowId> [options]                  Get workflow definition with steps',
      '  create [options]                            Create workflow from JSON',
      '  sync <workflowId> [options]                 Replace all nodes (same format as create)',
      '  fork <verb>                                 Create a fork / map fork nodes back to origin',
      '  patch <workflowId> [options]                Update workflow properties',
      '  approve <workflowId> [--yes] [--json]       Mark the test as human-verified (spec is right)',
      '  promote <workflowId> [--force] [--json]     Include the test in suite runs (promotion model; requires approval)',
      '  status <workflowId> [--set <status>]        Show or change the management status (blocked, paused, …)',
      '  events <workflowId> [--limit <n>] [--json]  Audit timeline: status changes + node (de)activations',
      '  revisions <list|get|restore> <workflowId> [number]  Immutable definition history',
      '  event ticket <wfId> <eventId> --add|--remove  Attach/detach tickets on an event',
      '  pause <workflowId> [--reason "…"] [--json]  Exclude the test from suite runs (idempotent)',
      '  unpause <workflowId> [--reason "…"] [--json] Re-include the test in suite runs (idempotent)',
      '  archive <workflowId> [--yes] [--json]       Soft-delete: hide from lists, skip in test suites (reversible)',
      '  unarchive <workflowId> [--json]             Restore a previously archived workflow',
      '  run <workflowId> [--cache-only] [options]   Trigger a workflow execution (--cache-only waits)',
      '  api-sequence <verb> <workflowId>            Manage the API sequence attached to a workflow',
      '  generate-api-sequence <workflowId> [opts]   (deprecated alias for `api-sequence generate`)',
      '  seed <sub-command>                          Create seed workflows',
      '  node <sub-command>                          Manage individual nodes',
      '  runs <sub-command>                          Query workflow run results',
      '  debug <runId|sub-command>                   Debug a run (overview/steps/cache/thoughts/logs/trace)',
      '',
      'List options:',
      '  --search <query>      Search by name',
      '  --flow-type <type>    Filter: standard, setup, seed, teardown',
      '  --status <status>     Filter: draft, approved',
      '  --page <n>            Page number (default: 1)',
      '  --page-size <n>       Page size (default: 25)',
      '  --json                Output raw JSON',
      '',
      'Get options:',
      '  --format json|markdown|spec  Output format (default: json)',
      '  --json                  Shorthand for --format json',
      `  --markdown              Shorthand for editable ${specExtension} spec output`,
      '  --output <file>         Write output to file',
      '',
      'Create options:',
      '  --from-stdin            Read workflow JSON from stdin (default)',
      '  --from-file <path>      Read workflow JSON from file',
      '  --api-only              Create an API-only workflow with a backing ApiSequence',
      '                          Requires: --name <name>',
      '                          Optional: --credential <id>, --guidance <text>,',
      '                                    --sequence-from-file <path> or --sequence-from-stdin',
      '                          Input shape: { steps, description?, stopOnFailure?, exports? }',
      '                          Returns: { workflowId, apiSequenceId, nodeId }',
      '  --tags <names>          Comma-separated tag names to assign (names, not IDs)',
      '  --tag-id <ids>          Comma-separated raw tag IDs (escape hatch)',
      '',
      '  Input: { name, nodes: [{ nodeType, configJson }] }',
      '  Run "canary workflow guide node-types" for available node types.',
      '  Run "canary workflow guide macros" for dynamic test data ($EMAIL, $RANDOM_STRING, etc.)',
      '',
      'Sync options:',
      '  --from-file <path>      Read nodes JSON from file',
      '  --from-stdin            Read nodes JSON from stdin',
      '  --no-snapshot           Skip creating a draft snapshot before sync',
      '  --propose               Create a proposal instead of applying directly',
      '  --title <title>         Proposal title (with --propose)',
      '  --description <desc>    Proposal description (with --propose)',
      '  --json                  Output raw JSON',
      '',
      '  Sync accepts the same minimal node format as create.',
      '  Edges, IDs, positions, and ordering are auto-generated when omitted.',
      '  Input: { nodes: [{ nodeType, configJson }] }',
      '',
      '  When using --propose on an existing workflow, run `canary workflow get',
      "  <id>` first and reuse each node's existing `id` for steps you intend",
      '  to keep. Only omit `id` for genuinely new steps. Without ID reuse the',
      '  proposal will diff as a full remove-everything/add-everything rewrite.',
      '',
      'Patch options:',
      '  --name <name>           Update workflow name',
      '  --description <desc>    Update description',
      '  --status <status>       Set status (approval axis). Valid:',
      '                            draft       — not yet approved',
      '                            approved    — a human verified the test checks the right thing',
      '                          (suite inclusion is derived eligibility — approved AND',
      '                           passed at least once — with a separate pause/unpause toggle;',
      '                           reliability is tracked automatically by health)',
      '  --flow-type <type>      Set type: standard, setup, seed, teardown',
      '  --seed-outputs-json <json>   Replace seedOutputsJson (array) — seed flows only',
      '  --seed-outputs-file <path>   Replace seedOutputsJson from a JSON file',
      '  --tags <names>          Replace tags with these names (comma-separated)',
      '  --tags-add <names>      Add these tag names to the existing set',
      '  --tags-remove <names>   Remove these tag names from the existing set',
      '  --tag-id <ids>          Raw tag IDs (comma-separated)',
      '  --json                  Output raw JSON',
      '',
      'Approve options:',
      '  --yes                   Confirm approval (required non-interactively)',
      '  --json                  Output raw JSON',
      '',
      '  Approval asserts a human verified the test checks the right thing.',
      '  Agents: describe the test to the user and confirm they personally',
      '  verified it before re-running with --yes.',
      '',
      'Promote options (promotion model):',
      '  --force                 Promote without requiring a successful run (skips the',
      '                          run gate; the approval gate can never be forced)',
      '  --json                  Output raw JSON',
      '',
      '  Promote includes the test in suite runs on the legacy promotion model.',
      '  Requires the test to be approved, and (without --force) a passing run.',
      '',
      'Status options (management status — the manual lifecycle axis):',
      '  --set <status>          Transition to: active, paused, blocked,',
      '                          under_construction, deprecated',
      '  --reason <text|@file>   Auditable reason recorded on the change event',
      '  --ticket <iss_…|url>    Attach a ticket to the event (repeatable) —',
      '                          an internal issue id or an external Linear/Jira URL',
      '  --json                  Output raw JSON',
      '',
      '  Distinct from approval (`patch --status`) and computed health. Any',
      '  non-active status excludes the flow from suite runs. Every change is',
      '  recorded as a workflow event (who/when/why) shown in the Comments tab',
      '  and via `workflow events`. Without --set, prints all three axes.',
      '',
      'Pause / unpause options:',
      '  --reason <text>         Auditable reason recorded on the change event',
      '  --json                  Output raw JSON',
      '',
      '  On the eligibility model, a flow is eligible for suite runs once it is',
      '  approved AND has passed at least once — no explicit promote step. Pause',
      '  excludes a flow from suite runs; unpause re-includes it. Both are',
      '  idempotent (equivalent to `status --set paused|active`). Suites always',
      '  execute the live definition — edits take effect immediately.',
      '',
      'Run options:',
      '  --cache-only            Replay workflow actions only and wait for completion; authentication may use AI login',
      '  --stream                Follow execution until completion',
      '  --json                  Output raw JSON',
      '',
      'API Sequence sub-commands:',
      '  api-sequence get <wf>                       Show config + steps',
      '  api-sequence update <wf> --from-file <f>    Replace config',
      '  api-sequence execute <wf>                   Run the sequence',
      '  api-sequence delete <wf>                    Delete the sequence',
      '  api-sequence set-api-only <wf> [--off]     Toggle workflow apiOnly flag',
      '  api-sequence generate <wf> [--prompt ...]   AI-generate a config (streams)',
      '',
      '  Run "canary workflow guide api-sequences" for step shape and template syntax.',
      '',
      'Seed sub-commands:',
      '  seed create [options]                        Create a seed workflow with outputs',
      '',
      'Seed create options:',
      '  --name <name>              Seed workflow name (required unless JSON input)',
      '  --description <desc>       Description',
      '  --output <key:name[:desc]> Declare an output (repeatable)',
      '  --outputs-json <json>      Full outputs array as inline JSON',
      '  --from-stdin               Read full config from stdin',
      '  --from-file <path>         Read full config from file',
      '  --no-teardown              Skip creating paired teardown workflow',
      '  --status <status>          Initial status (default: draft)',
      '  --json                     Output raw JSON',
      '',
      'Node sub-commands:',
      "  node patch <workflowId> <nodeId> [options]  Update a node's config",
      '  node deactivate <wfId> <nodeId> [--reason "…"]  Skip this node during runs (audited event)',
      '  node activate <wfId> <nodeId> [--reason "…"]    Re-enable a deactivated node (audited event)',
      '',
      'Node patch options:',
      '  --set-input key=value   Set an inputValue (repeatable)',
      '  --config \'{"..."}\'      Merge arbitrary configJson (partial update)',
      '  --json                  Output raw JSON',
      '',
      'Runs sub-commands:',
      '  runs list <workflowId>                    List runs for a workflow',
      '  runs get <workflowId> <runId>             Get run detail with steps',
      '  runs diagnostics <workflowId> <runId>     Get failure diagnostic',
      '',
      'Cache sub-commands:',
      '  cache status <workflowId>                                Show per-node cache version, health, recent failures',
      '  cache clear <workflowId> [--node <id>] [--below-version <n>] [--stale-only] [--mode all]',
      '                                                            Targeted cache invalidation (rows kept, isValid=false)',
      '  cache clear <workflowId> --mode hard --yes               DESTRUCTIVE: delete cache rows, strip playbookSteps,',
      '                                                            null lastRunId. Same as the UI "Reset flow" button',
      '  cache optimize <workflowId> [--run <runId>]              Enqueue the async cache-optimizer job',
      '  cache optimize-dry --run <runId> [--workflow <id>]       Run local deterministic optimizer dry-run',
      '  cache convergence-report <convergenceRunId> [--out <f>]  Markdown review doc for a cache-convergence run',
      '',
      'Common options:',
      '  --env <env>           Target environment (prod, dev, local)',
      '  --api-url <url>       API URL override',
      '  --token <key>         API token override',
    ].join('\n')
  );
}

export async function runWorkflow(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help') {
    printWorkflowHelp();
    return;
  }

  if (subCommand === 'debug' && hasFlag(rest, '--help', '-h')) {
    const { handleWorkflowDebug } = await import('./workflow-debug.js');
    await handleWorkflowDebug(rest, '', '');
    return;
  }

  if (hasFlag(argv, '--help', '-h')) {
    printWorkflowHelp();
    return;
  }

  if (subCommand === 'guide') {
    const sectionSlug = rest.find((a) => !a.startsWith('-'));
    const jsonOutput = hasFlag(rest, '--json');
    printGuide(WORKFLOW_GUIDE_SECTIONS, sectionSlug, jsonOutput);
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);

  switch (subCommand) {
    case 'list':
      await handleWorkflowList(rest, apiUrl, token);
      break;
    case 'get':
      await handleWorkflowGet(rest, apiUrl, token);
      break;
    case 'create':
      await handleWorkflowCreate(rest, apiUrl, token);
      break;
    case 'patch':
      await handlePatch(rest, apiUrl, token);
      break;
    case 'approve':
      await handleApprove(rest, apiUrl, token);
      break;
    case 'promote':
      await handlePromote(rest, apiUrl, token);
      break;
    case 'pause':
      await handlePauseToggle(rest, apiUrl, token, true);
      break;
    case 'unpause':
      await handlePauseToggle(rest, apiUrl, token, false);
      break;
    case 'archive':
      await handleArchive(rest, apiUrl, token);
      break;
    case 'unarchive':
      await handleUnarchive(rest, apiUrl, token);
      break;
    case 'run':
      await handleWorkflowRun(rest, apiUrl, token);
      break;
    case 'runs':
      await runWorkflowRuns(rest, apiUrl, token);
      break;
    case 'status': {
      const { handleWorkflowStatus } = await import('./workflow-status.js');
      await handleWorkflowStatus(rest, apiUrl, token);
      break;
    }
    case 'events': {
      const { handleWorkflowEvents } = await import('./workflow-status.js');
      await handleWorkflowEvents(rest, apiUrl, token);
      break;
    }
    case 'revisions': {
      const { handleWorkflowRevisions } = await import('./workflow-revisions.js');
      await handleWorkflowRevisions(rest, apiUrl, token);
      break;
    }
    case 'event': {
      const [eventSub, ...eventRest] = rest;
      if (eventSub === 'ticket') {
        const { handleWorkflowEventTicket } = await import('./workflow-status.js');
        await handleWorkflowEventTicket(eventRest, apiUrl, token);
      } else {
        console.error(`Unknown event sub-command: ${eventSub ?? '(none)'}`);
        printWorkflowHelp();
        process.exit(1);
      }
      break;
    }
    case 'node': {
      const [nodeSub, ...nodeRest] = rest;
      if (nodeSub === 'patch') {
        await handleNodePatch(nodeRest, apiUrl, token);
      } else if (nodeSub === 'deactivate' || nodeSub === 'activate') {
        // Audited toggle: hits the dedicated endpoint, which patches
        // configJson.deactivated AND records a workflow event with actor +
        // --reason. A deactivated node is skipped during runs (step status
        // `skipped`), letting you converge a flow node-by-node from the front.
        const { handleNodeDeactivateToggle } = await import('./workflow-status.js');
        await handleNodeDeactivateToggle(nodeRest, apiUrl, token, nodeSub === 'deactivate');
      } else {
        console.error(`Unknown node sub-command: ${nodeSub ?? '(none)'}`);
        printWorkflowHelp();
        process.exit(1);
      }
      break;
    }
    case 'seed': {
      const [seedSub, ...seedRest] = rest;
      if (seedSub === 'create') {
        await handleWorkflowSeedCreate(seedRest, apiUrl, token);
      } else {
        console.error(`Unknown seed sub-command: ${seedSub ?? '(none)'}`);
        printWorkflowHelp();
        process.exit(1);
      }
      break;
    }
    case 'sync':
      await handleWorkflowSync(rest, apiUrl, token);
      break;
    case 'fork': {
      const { handleWorkflowFork } = await import('./workflow-fork.js');
      await handleWorkflowFork(rest, apiUrl, token);
      break;
    }
    case 'proposals': {
      const { handleWorkflowProposals } = await import('./workflow-proposals.js');
      await handleWorkflowProposals(rest, apiUrl, token);
      break;
    }
    case 'generate-api-sequence':
      // Deprecated alias — forwards to `api-sequence generate`.
      await handleWorkflowGenerateApiSequence(rest, apiUrl, token);
      break;
    case 'api-sequence':
      await handleWorkflowApiSequence(rest, apiUrl, token);
      break;
    case 'cache': {
      const { handleWorkflowCache } = await import('./workflow-cache.js');
      await handleWorkflowCache(rest, apiUrl, token);
      break;
    }
    case 'debug': {
      const { handleWorkflowDebug } = await import('./workflow-debug.js');
      await handleWorkflowDebug(rest, apiUrl, token);
      break;
    }
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printWorkflowHelp();
      process.exit(1);
  }
}
